<template>
   <div v-if="isOpen" class="gts-ads-generator-overlay" @click.self="close">
      <div class="gts-ads-generator-modal">
         <div class="modal-header">
            <h3>GTS Ads Generator</h3>
            <button class="close-btn" @click="close">×</button>
         </div>
         <div class="modal-body">
            <div class="content-wrapper">
               <div class="left-panel">
                  <div class="canvas-container">
                     <canvas ref="canvas"></canvas>
                  </div>
               </div>
               <div class="right-panel">
                  <h4>Campaign Images</h4>
                  <div class="campaign-images-list">
                     <div class="campaign-image-item" v-for="campaign in campaigns" :key="campaign.uuid" @click="loadImageToCanvas(campaign.id)">
                        <img :src="`https://cdn.dev.reisetech.io/announcements/${campaign.id}.jpg`" :alt="campaign.name" />
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </div>
   </div>
</template>

<script>
export default {
   name: "GtsAdsGenerator",
   props: {
      campaigns: {
         type: Array,
         default: () => [],
      },
   },
   data() {
      return {
         isOpen: false,
         text: "",
         fontSize: 32,
         textColor: "#000000",
         bgColor: "#ffffff",
         backgroundImage: null,
         logoImage: null,
      };
   },
   mounted() {
      this.initCanvas();
      this.loadLogo();
   },
   computed: {
      AgencyLogo() {
         return "https://cdn.reisetech.io/logo_agency/18.jpg";
      },
   },
   watch: {
      text() {
         this.drawCanvas();
      },
      fontSize() {
         this.drawCanvas();
      },
      textColor() {
         this.drawCanvas();
      },
      bgColor() {
         this.drawCanvas();
      },
   },
   methods: {
      open() {
         this.isOpen = true;
      },
      close() {
         this.isOpen = false;
      },
      initCanvas(width = 800, height = 600) {
         const canvas = this.$refs.canvas;
         canvas.width = width;
         canvas.height = height;
         const ctx = canvas.getContext("2d");
         ctx.fillStyle = this.bgColor;
         ctx.fillRect(0, 0, canvas.width, canvas.height);
      },
      drawCanvas() {
         const canvas = this.$refs.canvas;
         const ctx = canvas.getContext("2d");

         // Clear canvas
         ctx.clearRect(0, 0, canvas.width, canvas.height);

         // Background
         if (this.backgroundImage) {
            ctx.drawImage(this.backgroundImage, 0, 0, canvas.width, canvas.height);
         } else {
            ctx.fillStyle = this.bgColor;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
         }

         // Text
         if (this.text) {
            ctx.fillStyle = this.textColor;
            ctx.font = `${this.fontSize}px Arial`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(this.text, canvas.width / 2, canvas.height / 2);
         }

         // Logo at bottom right (on top)
         if (this.logoImage) {
            const logoWidth = 120;
            const logoHeight = (this.logoImage.height / this.logoImage.width) * logoWidth;
            const padding = 20;
            ctx.drawImage(this.logoImage, canvas.width - logoWidth - padding, canvas.height - logoHeight - padding, logoWidth, logoHeight);
         }
      },
      loadLogo() {
         const img = new Image();
         img.crossOrigin = "anonymous";
         img.src = this.AgencyLogo;

         img.onload = () => {
            this.logoImage = img;
            this.drawCanvas();
         };

         img.onerror = () => {
            console.error("Failed to load logo");
         };
      },
      loadImageToCanvas(imageId) {
         const img = new Image();
         img.crossOrigin = "anonymous";
         img.src = `https://cdn.dev.reisetech.io/announcements/${imageId}.jpg`;

         img.onload = () => {
            this.backgroundImage = img;

            // Calculate canvas size to fit screen while maintaining ratio
            const maxWidth = 1200;
            const maxHeight = 800;
            let width = img.width;
            let height = img.height;

            const ratio = Math.min(maxWidth / width, maxHeight / height);
            if (ratio < 1) {
               width = width * ratio;
               height = height * ratio;
            }

            this.initCanvas(width, height);
            this.drawCanvas();
         };

         img.onerror = () => {
            console.error("Failed to load image");
         };
      },
      downloadImage() {
         const canvas = this.$refs.canvas;
         const link = document.createElement("a");
         link.download = "gts-ad.png";
         link.href = canvas.toDataURL("image/png");
         link.click();
      },
   },
};
</script>

<style lang="scss" scoped>
.gts-ads-generator-overlay {
   position: fixed;
   top: 0;
   left: 0;
   width: 100%;
   height: 100%;
   background: rgba(0, 0, 0, 0.5);
   display: flex;
   align-items: center;
   justify-content: center;
   z-index: 9999;
}

.gts-ads-generator-modal {
   background: var(--surface-level-1);
   width: calc(100% - 4rem);
   height: calc(100% - 4rem);
   margin: 2rem;
   overflow: hidden;
   display: flex;
   flex-direction: column;
   border-radius: 1rem;
}

.modal-header {
   display: flex;
   justify-content: space-between;
   align-items: center;
   padding: 1.5rem;
   border-bottom: 1px solid var(--gray-200);

   h3 {
      margin: 0;
      color: var(--txt-color);
      font-size: 1.25rem;
   }

   .close-btn {
      background: none;
      border: none;
      font-size: 2rem;
      line-height: 1;
      cursor: pointer;
      color: var(--txt-color);
      padding: 0;
      width: 2rem;
      height: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 0.5rem;
      transition: background 0.2s ease;

      &:hover {
         background: var(--gray-200);
      }
   }
}

.modal-body {
   padding: 1.5rem;
   overflow-y: auto;
   flex: 1;
}

.content-wrapper {
   display: flex;
   gap: 2rem;
   height: 100%;
}

.left-panel {
   flex: 1;
   display: flex;
   flex-direction: column;
   gap: 1.5rem;
}

.right-panel {
   width: 300px;
   display: flex;
   flex-direction: column;
   gap: 1rem;

   h4 {
      margin: 0;
      color: var(--txt-color);
      font-size: 1.1rem;
      padding-bottom: 0.5rem;
      border-bottom: 2px solid var(--gray-200);
   }
}

.campaign-images-list {
   display: flex;
   flex-direction: column;
   gap: 1rem;
   overflow-y: auto;
   padding-right: 0.5rem;

   &::-webkit-scrollbar {
      width: 6px;
   }

   &::-webkit-scrollbar-thumb {
      background: var(--gray-300);
      border-radius: 3px;
   }
}

.campaign-image-item {
   cursor: pointer;
   border-radius: 0.5rem;
   overflow: hidden;
   transition: transform 0.2s ease, box-shadow 0.2s ease;

   img {
      width: 100%;
      height: auto;
      display: block;
      object-fit: cover;
   }

   &:hover {
      transform: scale(1.02);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
   }
}

.canvas-container {
   display: flex;
   justify-content: center;
   border: 2px solid var(--gray-200);
   border-radius: 0.5rem;
   padding: 1rem;
   background: var(--gray-100);

   canvas {
      max-width: 100%;
      height: auto;
      border: 1px solid var(--gray-300);
      background: white;
   }
}

.controls {
   display: flex;
   flex-direction: column;
   gap: 1rem;
}

.input-group {
   display: flex;
   flex-direction: column;
   gap: 0.5rem;

   label {
      font-weight: 500;
      color: var(--txt-color);
      font-size: 0.9rem;
   }

   input {
      padding: 0.5rem;
      border: 1px solid var(--gray-300);
      border-radius: 0.5rem;
      background: var(--surface-level-2);
      color: var(--txt-color);
      font-size: 1rem;

      &:focus {
         outline: none;
         border-color: var(--primary-color);
      }
   }

   input[type="color"] {
      height: 40px;
      cursor: pointer;
   }
}

.button-group {
   display: flex;
   gap: 1rem;
   margin-top: 0.5rem;

   button {
      flex: 1;
      padding: 0.75rem 1.5rem;
      border: none;
      border-radius: 0.5rem;
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;

      &.btn-primary {
         background: var(--primary-color);
         color: white;

         &:hover {
            opacity: 0.9;
         }
      }

      &.btn-success {
         background: var(--success-color, #10b981);
         color: white;

         &:hover {
            opacity: 0.9;
         }
      }
   }
}
</style>
